import type { FC } from "react";

import { motion } from "framer-motion";

import type { OnboardingStep } from "../../../hooks/useOnboarding";

import { Button } from "../../common/Button";

const STEP_LABELS: Record<OnboardingStep, string> = {
  "create-org": "Organization created",
  done: "Setup complete",
  github: "GitHub connected",
  jira: "Jira connected",
  payment: "Billing enabled",
  "pending-invites": "Invites accepted",
};

interface DoneStepProps {
  completedSteps: Set<OnboardingStep>;
  onFinish: () => void;
}

export const DoneStep: FC<DoneStepProps> = ({ completedSteps, onFinish }) => {
  const checklistSteps: OnboardingStep[] = [
    "create-org",
    "github",
    "jira",
    "payment",
  ];
  const skippedSteps = checklistSteps.filter((s) => !completedSteps.has(s));

  return (
    <div className="space-y-6">
      <motion.div
        className="text-center"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <div className="mx-auto inline-grid size-20 place-items-center border-2 border-emerald-500 bg-emerald-500/20 shadow-arti-glow">
          <span className="font-mono text-3xl text-emerald-400">■</span>
        </div>
        <motion.h2
          className="mt-4 font-mono text-base font-bold uppercase tracking-widest text-white"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.3 }}
        >
          System initialized
        </motion.h2>
        <motion.p
          className="mt-1 font-mono text-[10px] uppercase tracking-widest text-emerald-400"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.3 }}
        >
          Workspace ready
        </motion.p>
      </motion.div>

      {/* Summary checklist with staggered reveal */}
      <div className="space-y-1">
        {checklistSteps.map((step, index) => {
          const isComplete = completedSteps.has(step);
          return (
            <motion.div
              key={step}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 + index * 0.08, duration: 0.2 }}
              className="flex items-center gap-3 border border-slate-700 bg-arti-background px-3 py-2"
            >
              <span
                className={`font-mono text-xs ${
                  isComplete ? "text-emerald-400" : "text-slate-600"
                }`}
              >
                {isComplete ? "■" : "□"}
              </span>
              <span
                className={`font-mono text-xs ${
                  isComplete ? "font-bold text-white" : "text-slate-500"
                }`}
              >
                {STEP_LABELS[step]}
              </span>
              {!isComplete && (
                <span className="ml-auto border border-slate-700 bg-slate-800 px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-600">
                  Skipped
                </span>
              )}
            </motion.div>
          );
        })}
      </div>

      {skippedSteps.length > 0 && (
        <motion.p
          className="text-center font-mono text-[10px] text-slate-500"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.3 }}
        >
          You can set up skipped items later in{" "}
          <a
            href="/settings"
            className="font-bold text-emerald-400 transition-colors hover:text-emerald-300"
          >
            Settings
          </a>
          .
        </motion.p>
      )}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.3 }}
      >
        <Button onClick={onFinish} fullWidth>
          Go to Dashboard
        </Button>
      </motion.div>
    </div>
  );
};
